import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Expense } from '@stfors/shared-types';
import { useExpenses } from '../api/queries/expenses';
import {
  useRecurringExpenses,
  useUpdateRecurringExpense,
  useDeleteRecurringExpense,
  labelForRrule,
  type RecurringExpenseTemplate,
} from '../api/queries/recurring-expenses';
import { useProjects } from '../api/queries/projects';
import { ApiError, api } from '../api/client';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { expenseStatusPills, expenseStatusRank } from '../utils/expense-status';
import { RecurringExpenseModal } from '../components/RecurringExpenseModal';
import { ExpenseDetailModal } from '../components/ExpenseDetailModal';
import { NewTravelExpenseModal } from '../components/NewTravelExpenseModal';
import { ExpenseImportModal } from '../components/ingest/ExpenseImportModal';
import {
  DocumentPreviewModal,
  type PreviewableFile,
} from '../components/DocumentPreviewModal';
import { BulkActionBar } from '../components/BulkActionBar';
import { IconAction } from '../components/IconAction';
import { ProjectOptions } from '../components/ProjectOptions';
import { SortableTh } from '../components/SortableTh';
import { ColumnSettings } from '../components/ColumnSettings';
import { useColVisibility, type ColumnDef } from '../hooks/useColVisibility';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useRowSelection } from '../hooks/useRowSelection';
import './ExpensesPage.css';

type ExpenseSortKey =
  | 'date'
  | 'project'
  | 'type'
  | 'tax_category'
  | 'vendor'
  | 'asset'
  | 'amount'
  | 'mileage'
  | 'receipt'
  | 'reimbursable'
  | 'notes';

// Menu order, not table order. There is no locked column here — the row itself
// opens the detail modal, so nothing depends on a particular cell surviving.
const EXPENSE_COLUMNS: readonly ColumnDef[] = [
  { key: 'date', label: 'Date' },
  { key: 'project', label: 'Project' },
  { key: 'type', label: 'Type' },
  { key: 'tax_category', label: 'Tax category' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'asset', label: 'Asset' },
  { key: 'amount', label: 'Amount' },
  { key: 'mileage', label: 'Mileage' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'reimbursable', label: 'Status' },
  { key: 'notes', label: 'Notes' },
];

// Asset and Tax category are empty on most rows — an asset only on equipment
// purchases, a tax category only once someone has classified the expense — so
// they cost width to say nothing. One click away in the menu.
const EXPENSE_DEFAULT_HIDDEN = ['asset', 'tax_category'];

type RuleSortKey =
  | 'name'
  | 'project'
  | 'repeats'
  | 'next_due'
  | 'amount'
  | 'reimbursable'
  | 'status';

import { formatMoney } from '../utils/money';
import { expenseTypeLabel } from '../utils/expense';
import { receiptFilename, receiptMime } from '../utils/receipt';
import { ReceiptIcon } from '../components/ReceiptIcon';

export function ExpensesPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [view, setView] = useState<'expenses' | 'rules'>('expenses');
  const [projectFilter, setProjectFilter] = useState<number | 'none' | undefined>(undefined);
  const [receiptPreview, setReceiptPreview] = useState<PreviewableFile | null>(null);
  /** Click-through detail modal — keep id so list refetches keep it in sync. */
  const [detailId, setDetailId] = useState<number | null>(null);
  /** "New travel" — a whole trip (costs + billable travel time) in one modal. */
  const [travelOpen, setTravelOpen] = useState(false);
  /** "Import receipts" — expenses from an import file + its receipts. */
  const [importOpen, setImportOpen] = useState(false);
  const rowSel = useRowSelection();
  const [bulkBusy, setBulkBusy] = useState(false);

  const { data: projectsData } = useProjects();
  const { data, isPending, error } = useExpenses(projectFilter);

  const clearRowSel = rowSel.clear;
  // Drop multi-select when the list scope changes so we never act on hidden rows.
  useEffect(() => {
    clearRowSel();
  }, [projectFilter, view, clearRowSel]);

  const rulesQ = useRecurringExpenses();
  const updateRule = useUpdateRecurringExpense();
  const deleteRule = useDeleteRecurringExpense();
  const [repeatSource, setRepeatSource] = useState<Expense | null>(null);
  const [editRule, setEditRule] = useState<RecurringExpenseTemplate | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const onDeleteRule = (t: RecurringExpenseTemplate) => {
    if (!confirm(`Delete repeating expense "${t.name}"? Already-generated expenses are kept.`)) return;
    setActionError(null);
    deleteRule.mutate(t.id, {
      onError: (err) =>
        setActionError(err instanceof ApiError && err.status === 403 ? "You can't change repeating expenses." : 'Delete failed.'),
    });
  };

  const projects = projectsData?.projects ?? [];
  const expenses = data?.expenses ?? [];
  const rules = rulesQ.data?.recurring_expense_templates ?? [];
  const selectedExpense = detailId === null
    ? null
    : (expenses.find((e) => e.id === detailId) ?? null);

  const approvableIds = rowSel.selectedIds.filter((id) => {
    const e = expenses.find((x) => x.id === id);
    return e != null && e.approved_at == null;
  });

  const bulkApproveExpenses = async () => {
    if (approvableIds.length === 0 || !can('expense.approve')) return;
    setActionError(null);
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        approvableIds.map((id) => api.post(`/api/expenses/${id}/approve`, {})),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setActionError(
          failed === results.length
            ? 'Could not approve the selected expenses.'
            : `Approved ${results.length - failed}; ${failed} failed.`,
        );
      }
      rowSel.clear();
    } finally {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      setBulkBusy(false);
    }
  };

  const vis = useColVisibility('expenses', EXPENSE_COLUMNS, EXPENSE_DEFAULT_HIDDEN);
  const expenseSort = useClientSort<Expense, ExpenseSortKey>(
    expenses,
    (e, key) => {
      switch (key) {
        case 'date': return e.date;
        case 'project':
          return e.project_id === null
            ? 'General business'
            : (e.project_case_number ?? `#${e.project_id}`);
        case 'type': return expenseTypeLabel(e.type);
        case 'tax_category': return e.tax_category ?? '';
        case 'vendor': return e.vendor ?? '';
        case 'asset': return e.asset_name ?? '';
        case 'amount': return Number(e.amount);
        case 'mileage': return e.mileage === null ? null : Number(e.mileage);
        case 'receipt': return e.receipt_path ? 1 : 0;
        case 'reimbursable': return expenseStatusRank(e);
        case 'notes': return e.notes ?? '';
        default: return '';
      }
    },
    { key: 'date', dir: 'desc' },
  );

  const rulesSort = useClientSort<RecurringExpenseTemplate, RuleSortKey>(
    rules,
    (t, key) => {
      switch (key) {
        case 'name': return t.name;
        case 'project':
          return t.project_id === null
            ? 'General business'
            : (t.project_case_number ?? `#${t.project_id}`);
        case 'repeats': return labelForRrule(t.rrule);
        case 'next_due': return t.is_active ? t.next_due_at : null;
        case 'amount': return Number(t.amount);
        case 'reimbursable': return t.reimbursable ? 1 : 0;
        case 'status': return t.is_active ? 1 : 0;
        default: return '';
      }
    },
    { key: 'name', dir: 'asc' },
  );

  const total = expenses.reduce((acc, e) => acc + Number(e.amount), 0);
  // `!e.billed` matters: without it an expense keeps counting as reimbursable
  // after it has been invoiced, so the figure only ever grows. `billed` is
  // computed per row by GET /api/expenses.
  const reimbursable = expenses
    .filter((e) => e.reimbursable && !e.billed)
    .reduce((acc, e) => acc + Number(e.amount), 0);
  const totalMileage = expenses
    .filter((e) => e.mileage !== null)
    .reduce((acc, e) => acc + Number(e.mileage ?? 0), 0);

  const expensesErrMsg =
    error instanceof ApiError && error.status === 403
      ? "You don't have permission to view expenses."
      : 'Failed to load expenses.';

  return (
    <section className="page-stack page-stack-wide">
      <div className="card ex-toolbar">
        <header className="ex-header">
          <div className="ex-title-group">
            <h2>Expenses</h2>
            <div className="ex-viewtoggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'expenses'}
                className={view === 'expenses' ? 'ex-viewtoggle-btn is-active' : 'ex-viewtoggle-btn'}
                onClick={() => setView('expenses')}
              >
                Expenses
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'rules'}
                className={view === 'rules' ? 'ex-viewtoggle-btn is-active' : 'ex-viewtoggle-btn'}
                onClick={() => setView('rules')}
              >
                Repeating rules{rules.length > 0 ? ` (${rules.length})` : ''}
              </button>
            </div>
          </div>
          {view === 'expenses' && (
            <div className="ex-header-controls">
              <select
                className="form-input ex-filter"
                value={projectFilter ?? 0}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'none') setProjectFilter('none');
                  else {
                    const n = Number(v);
                    setProjectFilter(n > 0 ? n : undefined);
                  }
                }}
              >
                <option value={0}>All projects</option>
                <option value="none">General business (no project)</option>
                <ProjectOptions projects={projects} />
              </select>
              {can('expense.write') && (
                <>
                  <button
                    type="button"
                    className="btn-charcoal-secondary"
                    onClick={() => setImportOpen(true)}
                  >
                    Import receipts
                  </button>
                  <button
                    type="button"
                    className="btn-charcoal-secondary"
                    onClick={() => setTravelOpen(true)}
                  >
                    New travel
                  </button>
                  <Link
                    to={`/expenses/new${projectFilter ? `?project_id=${projectFilter}` : ''}`}
                    className="btn-cyan-primary"
                  >
                    New expense
                  </Link>
                </>
              )}
            </div>
          )}
        </header>
      </div>

      {view === 'rules' ? (
        <>
          {actionError && (
            <div className="card"><p className="ex-error-banner" role="alert">{actionError}</p></div>
          )}
          {rulesQ.isPending ? (
            <div className="card"><p className="ex-status">Loading repeating rules…</p></div>
          ) : rulesQ.error ? (
            <div className="card"><p className="ex-status ex-error">Failed to load repeating rules.</p></div>
          ) : rules.length === 0 ? (
            <div className="card">
              <div className="ex-empty">
                <p>No repeating expenses yet.</p>
                <p className="ex-empty-meta">
                  Turn on <strong>Repeat</strong> when you create a new expense, or open an expense
                  and choose <strong>Repeat</strong>.
                </p>
              </div>
            </div>
          ) : (
            <div className="card card-tight ex-table-card table-scroll-card">
              <TableScroll id="expenses-rules">
                <table className="ex-table">
                  <thead>
                    <tr>
                      <SortableTh column="name" label="Name" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} />
                      <SortableTh column="project" label="Project" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} />
                      <SortableTh column="repeats" label="Repeats" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} />
                      <SortableTh column="next_due" label="Next due" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} />
                      <SortableTh column="amount" label="Amount" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} numeric className="ex-numeric" />
                      <SortableTh column="reimbursable" label="Reimbursable" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} />
                      <SortableTh column="status" label="Status" sortKey={rulesSort.sortKey} sortDir={rulesSort.sortDir} onSort={rulesSort.toggle} />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rulesSort.sorted.map((t) => (
                      <tr key={t.id} className={t.is_active ? '' : 'ex-row-muted'}>
                        <td>{t.name}</td>
                        <td>
                          {t.project_id === null
                            ? <span className="ex-general-business">General business</span>
                            : <code>{t.project_case_number ?? `#${t.project_id}`}</code>}
                        </td>
                        <td>{labelForRrule(t.rrule)}</td>
                        <td>{t.is_active ? fmtDate(t.next_due_at) : '—'}</td>
                        <td className="ex-numeric">{formatMoney(t.amount)}</td>
                        <td>{t.reimbursable ? 'Yes' : '—'}</td>
                        <td>
                          {t.is_active
                            ? <span className="ex-pill ex-pill-success">active</span>
                            : <span className="ex-pill">paused</span>}
                        </td>
                        <td className="ex-actions">
                          {can('expense.write') && (
                            <>
                              <IconAction
                                action={t.is_active ? 'pause' : 'resume'}
                                onClick={() => updateRule.mutate({ id: t.id, is_active: !t.is_active })}
                                disabled={updateRule.isPending}
                                iconSize={16}
                              />
                              <IconAction
                                action="edit"
                                onClick={() => setEditRule(t)}
                                iconSize={16}
                              />
                              <IconAction
                                action="delete"
                                onClick={() => onDeleteRule(t)}
                                disabled={deleteRule.isPending}
                                iconSize={16}
                              />
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </div>
          )}
        </>
      ) : isPending ? (
        <div className="card"><p className="ex-status">Loading expenses…</p></div>
      ) : error ? (
        <div className="card"><p className="ex-status ex-error">{expensesErrMsg}</p></div>
      ) : (
        <>
          <div className="card">
            <div className="ex-summary">
              <div className="ex-summary-card">
                <div className="ex-summary-label">Total</div>
                <div className="ex-summary-value">{formatMoney(total.toFixed(2))}</div>
              </div>
              <div className="ex-summary-card">
                <div className="ex-summary-label">Reimbursable</div>
                <div className="ex-summary-value ex-summary-warn">
                  {formatMoney(reimbursable.toFixed(2))}
                </div>
              </div>
              {totalMileage > 0 && (
                <div className="ex-summary-card">
                  <div className="ex-summary-label">Mileage</div>
                  <div className="ex-summary-value">{totalMileage.toFixed(2)} mi</div>
                </div>
              )}
            </div>
          </div>

          {expenses.length === 0 ? (
            <div className="card">
              <div className="ex-empty">
                <p>No expenses {projectFilter === 'none' ? 'for general business' : projectFilter ? 'for this project' : 'yet'}.</p>
                <p className="ex-empty-meta">
                  {can('expense.write')
                    ? 'Click "New expense" above to record one.'
                    : 'Expenses are recorded by examiners and contractors.'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {actionError && view === 'expenses' && (
                <div className="card"><p className="ex-error-banner" role="alert">{actionError}</p></div>
              )}
              <BulkActionBar
                count={rowSel.count}
                onClear={rowSel.clear}
                busy={bulkBusy}
                hint={
                  can('expense.approve')
                    ? (approvableIds.length > 0
                      ? `${approvableIds.length} ready to approve`
                      : 'None of the selected are pending approval')
                    : undefined
                }
              >
                {can('expense.approve') && (
                  <button
                    type="button"
                    className="btn-cyan-primary"
                    disabled={bulkBusy || approvableIds.length === 0}
                    onClick={() => void bulkApproveExpenses()}
                  >
                    {bulkBusy ? 'Approving…' : `Approve${approvableIds.length > 0 ? ` (${approvableIds.length})` : ''}`}
                  </button>
                )}
              </BulkActionBar>
            <div className="card card-tight ex-table-card table-scroll-card">
              <div className="ex-table-bar">
                <p className="ex-table-hint">
                  Click a row for details. Hold <kbd>Ctrl</kbd> (Mac: <kbd>⌘</kbd>) and click to multi-select for bulk approve.
                </p>
                <ColumnSettings vis={vis} />
              </div>
              <TableScroll id="expenses">
                <table className="ex-table">
                  <thead>
                    <tr>
                      {vis.isVisible('date') && <SortableTh column="date" label="Date" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('project') && <SortableTh column="project" label="Project" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('type') && <SortableTh column="type" label="Type" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('tax_category') && <SortableTh column="tax_category" label="Tax category" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('vendor') && <SortableTh column="vendor" label="Vendor" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('asset') && <SortableTh column="asset" label="Asset" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('amount') && <SortableTh column="amount" label="Amount" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} numeric className="ex-numeric" />}
                      {vis.isVisible('mileage') && <SortableTh column="mileage" label="Mileage" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} numeric className="ex-numeric" />}
                      {vis.isVisible('receipt') && <SortableTh column="receipt" label="Receipt" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('reimbursable') && <SortableTh column="reimbursable" label="Status" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                      {vis.isVisible('notes') && <SortableTh column="notes" label="Notes" sortKey={expenseSort.sortKey} sortDir={expenseSort.sortDir} onSort={expenseSort.toggle} />}
                    </tr>
                  </thead>
                  <tbody>
                    {expenseSort.sorted.map((e) => (
                      <tr
                        key={e.id}
                        className={`ex-row-click${rowSel.isSelected(e.id) ? ' table-row-selected' : ''}`}
                        tabIndex={0}
                        role="button"
                        aria-selected={rowSel.isSelected(e.id)}
                        aria-label={`Open expense ${fmtDate(e.date)} ${formatMoney(e.amount)}`}
                        onMouseDown={rowSel.suppressTextSelect}
                        onClick={(ev) => {
                          const ordered = expenseSort.sorted.map((x) => x.id);
                          if (rowSel.handleRowClick(ev, e.id, ordered)) return;
                          setDetailId(e.id);
                        }}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            setDetailId(e.id);
                          }
                        }}
                      >
                        {vis.isVisible('date') && <td>{fmtDate(e.date)}</td>}
                        {vis.isVisible('project') && (
                          <td>
                            {e.project_id === null ? (
                              <span className="ex-general-business">General business</span>
                            ) : (
                              <code>{e.project_case_number ?? `#${e.project_id}`}</code>
                            )}
                          </td>
                        )}
                        {vis.isVisible('type') && <td>{expenseTypeLabel(e.type)}</td>}
                        {vis.isVisible('tax_category') && (
                          <td>
                            {e.tax_category ? (
                              <span className="ex-tax-cat">{e.tax_category}</span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {vis.isVisible('vendor') && <td>{e.vendor ?? '—'}</td>}
                        {vis.isVisible('asset') && <td>{e.asset_name ?? '—'}</td>}
                        {vis.isVisible('amount') && (
                          <td className="ex-numeric ex-amount">{formatMoney(e.amount)}</td>
                        )}
                        {vis.isVisible('mileage') && (
                          <td className="ex-numeric">
                            {e.mileage !== null ? `${Number(e.mileage).toFixed(2)} mi` : '—'}
                          </td>
                        )}
                        {vis.isVisible('receipt') && (
                        <td className="ex-receipt-cell">
                          {e.receipt_path ? (
                            <button
                              type="button"
                              className="ex-receipt-btn"
                              title={`Preview receipt: ${receiptFilename(e)}`}
                              aria-label={`Preview receipt ${receiptFilename(e)}`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setReceiptPreview({
                                  downloadUrl: `/api/expenses/${e.id}/receipt`,
                                  filename: receiptFilename(e),
                                  mimeType: receiptMime(e),
                                });
                              }}
                            >
                              <ReceiptIcon
                                mimeType={
                                  receiptFilename(e).toLowerCase().endsWith('.pdf')
                                    ? 'application/pdf'
                                    : receiptMime(e)
                                }
                              />
                            </button>
                          ) : (
                            <span className="ex-unlinked">—</span>
                          )}
                        </td>
                        )}
                        {vis.isVisible('reimbursable') && (
                        <td>
                          <span className="ex-pill-stack">
                            {/* Reimbursed (client paid the invoice, or marked by
                                hand) stands alone; Billed until then. The rule
                                lives in utils/expense-status.ts. */}
                            {expenseStatusPills(e).map((p) => (
                              <span
                                key={p.label}
                                className={`ex-pill${p.tone === 'neutral' ? '' : ` ex-pill-${p.tone}`}`}
                                title={p.title}
                              >
                                {p.label}
                              </span>
                            ))}
                            {expenseStatusPills(e).length === 0 && '—'}
                          </span>
                        </td>
                        )}
                        {/* The width cap lives on the inner block because a
                            <td> ignores max-width — see the CSS. */}
                        {vis.isVisible('notes') && (
                          <td className="ex-desc">
                            <div className="ex-desc-text">{e.notes ?? ''}</div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </div>
            </>
          )}
        </>
      )}

      {selectedExpense && (
        <ExpenseDetailModal
          expense={selectedExpense}
          onClose={() => setDetailId(null)}
          onDuplicate={(ex) => navigate(`/expenses/new?from=${ex.id}`)}
          onRepeat={(ex) => {
            setRepeatSource(ex);
            // Keep detail open under the repeat modal, or close — close is cleaner.
            setDetailId(null);
          }}
        />
      )}
      {importOpen && <ExpenseImportModal onClose={() => setImportOpen(false)} />}
      {travelOpen && (
        <NewTravelExpenseModal
          onClose={() => setTravelOpen(false)}
          initialProjectId={projectFilter}
        />
      )}
      {repeatSource && (
        <RecurringExpenseModal mode="repeat" expense={repeatSource} onClose={() => setRepeatSource(null)} />
      )}
      {editRule && (
        <RecurringExpenseModal mode="edit" template={editRule} onClose={() => setEditRule(null)} />
      )}
      {receiptPreview && (
        <DocumentPreviewModal
          file={receiptPreview}
          onClose={() => setReceiptPreview(null)}
        />
      )}
    </section>
  );
}
